import { PROACTIVITY_LEVELS } from "../api/types.js";
import type { AssistantSettings, VoiceCatalog } from "../api/types.js";
import { ErrorState, Loading, ThemeSelect, useToast } from "@uzverse/ui";
import { useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ErrorNote, useCommonT, useErrorText } from "../components/common.js";
import { AssistantModelPicker } from "../components/ModelPicker.js";
import { SecuritySettings } from "../components/SecuritySettings.js";
import { AvatarCreator } from "../components/AvatarCreator.js";
import { LanguageSwitch } from "../components/Layout.js";
import { useSession } from "../state/session.js";
import { useMyAvatarSync } from "../state/useMyAvatarSync.js";
import { decodeAppearance, encodeAppearance } from "../world/humans/spec.js";
import type { AvatarSpec } from "../world/humans/spec.js";
import { saveMyAvatar, useMyAvatar } from "../world/humans/store.js";
import { useLoad } from "../state/useLoad.js";
import { playPreview } from "../voice/preview.js";

/** Voices whose languages match the UI language (tag prefix, e.g. "tr" matches "tr-TR"). */
export function voicesForLanguage(catalog: VoiceCatalog, language: string) {
  return catalog.voices.filter((v) =>
    v.languages.some((l) => l.toLowerCase().split("-")[0] === language),
  );
}

export function SettingsPage() {
  const { t, i18n } = useTranslation("web");
  const errorText = useErrorText();
  const tc = useCommonT();
  const toast = useToast();
  const { services, signOut } = useSession();
  const { api } = services;
  const settings = useLoad(() => api.getAssistantSettings());
  const catalog = useLoad(() => api.listVoices());
  useMyAvatarSync();
  const mine = useMyAvatar();
  const [myDraft, setMyDraft] = useState<AvatarSpec | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const draft = settings.data;
  const update = (patch: Partial<AssistantSettings>) => {
    settings.setData((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    setError(null);
    try {
      settings.setData(await api.saveAssistantSettings(draft));
      toast.show(t("settings.saved"));
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function saveCharacter() {
    if (!myDraft) return;
    setError(null);
    try {
      await saveMyAvatar(myDraft);
      setMyDraft(null);
      toast.show(t("avatar.saved"));
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function preview() {
    if (!draft?.voice) return;
    setPreviewing(true);
    try {
      playPreview(await api.previewVoice(draft.voice.providerId, draft.voice.voiceId));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPreviewing(false);
    }
  }

  const voices = catalog.data ? voicesForLanguage(catalog.data, i18n.language) : [];
  const providerName = (id: string) => id;
  const selectedKey = draft?.voice ? `${draft.voice.providerId}::${draft.voice.voiceId}` : "";
  const selectedProvider = draft?.voice
    ? catalog.data?.providers.find((p) => p.id === draft.voice?.providerId)
    : undefined;
  const canPreview = selectedProvider?.capabilities["preview"] === true;

  return (
    <section aria-labelledby="settings-title" className="stack">
      <h1 id="settings-title">{t("settings.title")}</h1>
      <div className="card stack">
        <div className="row row-between">
          <span className="field-label">{t("settings.language")}</span>
          <LanguageSwitch />
        </div>
        <div className="row row-between">
          <span className="field-label">{tc("ui.theme.label")}</span>
          <ThemeSelect t={tc} />
        </div>
      </div>
      <section
        className="card stack"
        aria-labelledby="my-character-title"
        data-testid="my-character"
      >
        <h2 id="my-character-title">{t("avatar.title")}</h2>
        <p className="muted small">{t("avatar.intro")}</p>
        <AvatarCreator
          spec={myDraft ?? mine.spec}
          onChange={setMyDraft}
          label={mine.name || t("avatar.title")}
        />
        <p className="muted small">{t("avatar.note")}</p>
        <div className="row">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!myDraft}
            onClick={() => void saveCharacter()}
          >
            {t("avatar.save")}
          </button>
        </div>
      </section>
      {draft ? (
        <form className="card stack" onSubmit={(e) => void save(e)}>
          <h2>{t("settings.assistant.title")}</h2>
          <label className="field">
            <span>{t("settings.assistant.name")}</span>
            <input
              value={draft.name}
              onChange={(e) => update({ name: e.target.value })}
              maxLength={40}
              required
            />
          </label>
          <div className="field">
            <span>{t("avatar.assistantLook")}</span>
            <AvatarCreator
              spec={decodeAppearance(draft.appearance, draft.name)}
              onChange={(spec) => update({ appearance: encodeAppearance(spec) })}
              label={draft.name}
            />
            <small className="muted">{t("avatar.assistantNote")}</small>
          </div>

          <fieldset className="field">
            <legend>{t("settings.assistant.proactivity")}</legend>
            {PROACTIVITY_LEVELS.map((level) => (
              <label key={level} className="check">
                <input
                  type="radio"
                  name="proactivity"
                  value={level}
                  checked={draft.proactivity === level}
                  onChange={() => update({ proactivity: level })}
                />
                <span>
                  {t(`settings.assistant.proactivityLevel.${level}`)}
                  <small className="muted block">
                    {t(`settings.assistant.proactivityHint.${level}`)}
                  </small>
                </span>
              </label>
            ))}
          </fieldset>

          <h2>{t("settings.voice.title")}</h2>
          <label className="field">
            <span>{t("settings.voice.select")}</span>
            <select
              value={selectedKey}
              onChange={(e) => {
                const [providerId, voiceId] = e.target.value.split("::");
                update({ voice: providerId && voiceId ? { providerId, voiceId } : null });
              }}
            >
              <option value="">{t("settings.voice.none")}</option>
              {voices.map((v) => (
                <option key={`${v.providerId}::${v.id}`} value={`${v.providerId}::${v.id}`}>
                  {v.name} ({v.paid ? t("settings.voice.paid") : t("settings.voice.free")},{" "}
                  {t("settings.voice.by", { provider: providerName(v.providerId) })})
                </option>
              ))}
            </select>
            {catalog.data && voices.length === 0 && (
              <small className="muted">{t("settings.voice.noVoices")}</small>
            )}
          </label>
          {canPreview && (
            <button
              type="button"
              className="btn"
              onClick={() => void preview()}
              disabled={previewing}
            >
              {previewing ? t("settings.voice.previewing") : t("settings.voice.preview")}
            </button>
          )}

          <ErrorNote message={error ?? (settings.error ? errorText(settings.error) : null)} />
          <div className="row">
            <button type="submit" className="btn btn-primary">
              {t("actions.save", { ns: "common" })}
            </button>
          </div>
        </form>
      ) : settings.error ? (
        <ErrorState t={tc} message={errorText(settings.error)} onRetry={settings.reload} />
      ) : (
        <Loading t={tc} />
      )}
      <section className="card stack" aria-labelledby="model-title">
        <h2 id="model-title">{t("modelPick.title")}</h2>
        <p className="muted small">{t("modelPick.intro")}</p>
        <AssistantModelPicker />
      </section>
      <SecuritySettings />
      <div className="card row row-between">
        <span className="field-label">{t("nav.signOut")}</span>
        <button type="button" className="btn" onClick={() => void signOut()}>
          {t("nav.signOut")}
        </button>
      </div>
      <p>
        <Link className="link" to="/settings/advanced">
          {t("settings.advancedLink")}
        </Link>
      </p>
    </section>
  );
}
